/* eslint-disable import/no-extraneous-dependencies */
/**
 * main.js
 *
 * Bootstraps plugins, then mounts the app. Dev mode awaits MSW worker
 * startup before mount to avoid races between worker registration and
 * the app's first Parse.Cloud.run calls.
 */
import { createApp } from 'vue';
import { registerPlugins } from '@/plugins';
import { initClarity } from '@/plugins/clarity';
import { initSentry } from '@/plugins/sentry';
import { reportWebVitals } from '@/plugins/webVitals';
import router from '@/router';
import { isPublicReportPath } from '@/lib/bootSession';
import '@/assets/styles/tailwind.css';
// Components
import App from './App.vue';

async function enableMocking() {
  if (!import.meta.env.DEV) return undefined;
  if (import.meta.env.VITE_MSW_ENABLED !== 'true') return undefined;
  const { worker } = await import('./mocks/browser');
  return worker.start({ onUnhandledRequest: 'bypass' });
}

const app = createApp(App);

// Session recording and error reporting stay off the public report pages:
// they show a patient's health data to someone outside the clinic, and their
// URL is half of the key that opens it.
if (!isPublicReportPath(window.location.pathname)) {
  initSentry(app, router);
  // Initialize Clarity before mounting the app
  initClarity();
  reportWebVitals();
}

// Plugins wait for the mock server as well as the mount does, because
// installing the router starts the first navigation there and then, and its
// guard asks the server who is signed in. Installed first, a page opened
// straight from its address had that question go to a server that was not
// there yet: the guard signed the doctor out, sent them to login, and login,
// asked again once the mocks were up, forwarded them home. Without mocks this
// resolves at once, so the order is unchanged outside development.
enableMocking().then(() => {
  registerPlugins(app);
  app.mount('#app');
});
